// src/portal/useFeatureSelection.js
//
// CLICK A SHAPE, HIGHLIGHT A SHAPE. Shared by both portal maps.
//
// ── WHY A HOOK AND NOT A SHARED COMPONENT ───────────────────────────────────
//
// The selection STYLE was already shared: IS_SELECTED, SELECTED_STATE_KEY, the
// `promoteId: "ordinal"` source option and the width/radius paint all live in
// portalMapStyle.js inside addSubmissionLayers, which BOTH maps already call.
// What was not shared was the wiring — the click handlers and the highlight
// effect — and it lived only in PortalSubmitterMap.
//
// Merging the two components was the alternative and is worse:
// PortalReviewerMap carries imagery and an MlDraw instance, so a shared
// component would be one component with two modes. Copying the wiring was the
// other alternative and is worse still: FOUR of the nine mutations that landed
// the tag control were against exactly these lines, and a second copy is a
// second place for both bugs below to come back.
//
// ── THE TWO THINGS THAT GO WRONG SILENTLY ───────────────────────────────────
//
// A STALE CLOSURE. `map.on("click", …)` is registered ONCE, inside the load
// handler. Closing over the callback directly pins the first render's version,
// so selection appears to work once and then stops. The callback is read from a
// ref for exactly this reason.
//
// A LEFT-BEHIND HIGHLIGHT. Feature state persists until it is removed, so
// setting the new feature without clearing the old leaves two shapes looking
// selected, with nothing to say which one a subsequent action will apply to.
//
// ── A HOOK WITH NO CALLBACK IS A READ-ONLY MAP ──────────────────────────────
//
// `onSelectFeature: null` registers no click handlers and reports nothing.
// PortalReviewPage mounts PortalSubmitterMap that way for a submitted revision,
// and PortalReviewerMap is mounted that way until the markup form lands. That
// is dormancy, not an inert control: nothing is on screen offering an action it
// cannot perform.

import { useEffect, useRef } from "react";
import { SUBMISSION_SOURCE, SUBMISSION_LAYER_IDS, SELECTED_STATE_KEY } from "./portalMapStyle";

/**
 * @param {object} p
 * @param {{current: any}} p.mapRef        ref holding the maplibre Map, or null
 * @param {Array}  p.features              redraw trigger; the source owns the data
 * @param {number|null} p.selectedOrdinal
 * @param {Function|null} p.onSelectFeature  called with an ordinal, or null for
 *   a click on empty map. null/undefined means a read-only map.
 * @param {Function|null} [p.isSuspended]  answers true while some OTHER tool
 *   owns the click. See "SELECTION YIELDS" below.
 * @returns {{registerClickHandlers: Function}} `registerClickHandlers(map)` must
 *   be called from the map's own `load` handler, because layer-scoped handlers
 *   can only be attached once the layers exist.
 */
export function useFeatureSelection({
  mapRef, features, selectedOrdinal = null, onSelectFeature = null, isSuspended = null,
}) {
  // THE REF IS THE STALE-CLOSURE GUARD. Assigned on every render so the handler
  // registered once at load always reads the current callback.
  const onSelectRef = useRef(onSelectFeature);
  onSelectRef.current = onSelectFeature;
  const appliedSelection = useRef(null);

  const isSuspendedRef = useRef(isSuspended);
  isSuspendedRef.current = isSuspended;

  /**
   * Called from the map's load handler, once the submission layers exist.
   *
   * ── NOTHING IS REGISTERED WHEN NOBODY IS LISTENING ────────────────────────
   *
   * A DORMANT FEATURE THAT STILL REGISTERS LIVE EVENT HANDLERS IS NOT DORMANT.
   *
   * This registered four click handlers unconditionally. PortalReviewerMap
   * adopted the hook one commit before anything passed it a callback, and that
   * was described as "dormant" -- true of the HIGHLIGHT, false of the handlers,
   * which were on the map from the moment it loaded, deselecting on every
   * click, next to an MlDraw that was about to arrive. The guard below is the
   * one change that would have prevented the whole collision.
   */
  function registerClickHandlers(map) {
    if (!onSelectRef.current) return;
    // Registered against the three submission layers rather than the map, so a
    // click on the basemap does not hit-test every layer in the style.
    for (const id of SUBMISSION_LAYER_IDS) {
      map.on("click", id, (e) => {
        if (isSuspendedRef.current?.()) return;
        const ord = e.features?.[0]?.properties?.ordinal;
        // A click is CONSUMED whether or not it lands on a feature we can
        // identify; otherwise the map-level handler below also fires and
        // immediately deselects what was just selected.
        e.preventDefault?.();
        if (onSelectRef.current && Number.isInteger(ord)) onSelectRef.current(ord);
      });
      map.on("mouseenter", id, () => { map.getCanvas().style.cursor = onSelectRef.current ? "pointer" : ""; });
      map.on("mouseleave", id, () => { map.getCanvas().style.cursor = ""; });
    }
    // Empty map clears the selection — the way out of a selection without
    // needing a named control for it.
    map.on("click", (e) => {
      // SELECTION YIELDS. `preventDefault` cannot help here: MapLibre's fire()
      // runs every listener unconditionally, and MlDraw's own click handler is
      // registered FIRST -- by addControl, before this hook is called -- so it
      // has already run and never sets the flag. The only thing that can
      // arbitrate is asking whether another tool currently owns the click.
      if (isSuspendedRef.current?.()) return;
      if (e.defaultPrevented) return;
      if (onSelectRef.current) onSelectRef.current(null);
    });
  }

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const prev = appliedSelection.current;
    if (prev !== null && prev !== selectedOrdinal) {
      map.setFeatureState({ source: SUBMISSION_SOURCE, id: prev }, { [SELECTED_STATE_KEY]: false });
    }
    // Number.isInteger, not truthiness: ordinal 0 is the FIRST feature and the
    // one most submissions have.
    if (Number.isInteger(selectedOrdinal)) {
      map.setFeatureState({ source: SUBMISSION_SOURCE, id: selectedOrdinal }, { [SELECTED_STATE_KEY]: true });
    }
    appliedSelection.current = Number.isInteger(selectedOrdinal) ? selectedOrdinal : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrdinal, features]);

  return { registerClickHandlers };
}
